/**
 * Live settlement examples for /learn (#777): pick a few real outcomes from outcomeMeta whose resolution time (from
 * describe/, i.e. the outcome's own on-chain description) is still ahead, so the page can show how a real market
 * says it will settle. Pure: no React, no network.
 */
import type { OutcomeDescription } from '../describe';
import type { OutcomeSpec } from '../hl/types';

export interface LearnExample {
  meta: OutcomeSpec;
  desc: OutcomeDescription;
  /** Who settles it: the protocol (no venue) or the venue's deployer. */
  settledBy: 'protocol' | 'venue';
}

/** Rank: protocol recurring first (best documented), then crypto price, then the rest; soonest resolution first. */
function rank(e: LearnExample): number {
  if (e.settledBy === 'protocol') return 0;
  if (e.desc.category === 'crypto price') return 1;
  return 2;
}

/**
 * Up to `max` outcomes with a parseable resolution time after `now`, best first. Outcomes that are part of a
 * question are skipped (their own text is only a label), as are raw (un-described) ones.
 */
export function pickExamples(
  outcomes: readonly OutcomeSpec[] | undefined,
  describe: (o: OutcomeSpec) => OutcomeDescription,
  now: Date,
  max = 6,
): LearnExample[] {
  const out: LearnExample[] = [];
  for (const meta of outcomes ?? []) {
    let desc: OutcomeDescription;
    try {
      desc = describe(meta);
    } catch {
      continue;
    }
    if (desc.raw || desc.question !== undefined || !desc.resolvesAt) continue;
    if (desc.resolvesAt.getTime() <= now.getTime()) continue;
    out.push({ meta, desc, settledBy: meta.venue ? 'venue' : 'protocol' });
  }
  out.sort((a, b) => rank(a) - rank(b) || a.desc.resolvesAt!.getTime() - b.desc.resolvesAt!.getTime());
  // Variety: at most two per rank bucket before filling up with the rest.
  const picked: LearnExample[] = [];
  const perRank = new Map<number, number>();
  for (const e of out) {
    const r = rank(e);
    if ((perRank.get(r) ?? 0) >= 2) continue;
    perRank.set(r, (perRank.get(r) ?? 0) + 1);
    picked.push(e);
    if (picked.length >= max) return picked;
  }
  for (const e of out) {
    if (picked.length >= max) break;
    if (!picked.includes(e)) picked.push(e);
  }
  return picked;
}
